import {describePageError} from '@common/http/errors/page-error-kind';
import {queryClient} from '@common/http/query-client';
import {Button, LinkButton} from '@shadcn/button/button';
import {Empty} from '@shadcn/empty/empty';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {Trans} from '@ui/i18n/trans';
import {CircleAlertIcon, LockIcon, MailCheckIcon} from 'lucide-react';
import {useState} from 'react';

export function PageErrorMessage({error}: {error?: unknown}) {
  const [isRetrying, setIsRetrying] = useState(false);
  const offline =
    typeof navigator !== 'undefined' && navigator.onLine === false;
  const {kind, serverMessage} = describePageError(error, offline);
  const viewingAs = Boolean(getBootstrapData()?.impersonator);
  const rawDetail =
    serverMessage ??
    (error instanceof Error
      ? error.message
      : error != null
        ? String(error)
        : null);
  const detail =
    rawDetail && !/Minified React error/i.test(rawDetail) ? rawDetail : null;

  const handleRetry = async () => {
    setIsRetrying(true);
    await queryClient.removeQueries();
    window.location.reload();
  };

  const retryButton = (
    <Button
      variant="outline"
      onClick={() => handleRetry()}
      disabled={isRetrying}
    >
      <Trans message="Retry" />
    </Button>
  );

  if (kind === 'emailNotVerified') {
    return (
      <Empty.Root>
        <Empty.Header>
          <Empty.Media>
            <MailCheckIcon />
          </Empty.Media>
          <Empty.Title>
            <Trans message="Verify your email to continue" />
          </Empty.Title>
          <Empty.Description>
            <Trans message="We sent a code to your email address. Enter it once, then this page opens normally." />
          </Empty.Description>
        </Empty.Header>
        <Empty.Content className="flex-row justify-center gap-2">
          <LinkButton to="/verify-email">
            <Trans message="Verify email" />
          </LinkButton>
          {retryButton}
        </Empty.Content>
      </Empty.Root>
    );
  }

  if (kind === 'forbidden') {
    return (
      <Empty.Root>
        <Empty.Header>
          <Empty.Media>
            <LockIcon />
          </Empty.Media>
          <Empty.Title>
            <Trans message="You don't have access to this page" />
          </Empty.Title>
          <Empty.Description>
            {viewingAs ? (
              <Trans message="This page is not open to the person you are viewing as. Return to admin to manage it from there." />
            ) : (
              <Trans message="Your account is not allowed to open this page. If you think this is a mistake, contact the site admin." />
            )}
            {detail ? (
              <span className="mt-2 block text-xs text-muted-foreground">
                {detail}
              </span>
            ) : null}
          </Empty.Description>
        </Empty.Header>
        <Empty.Content className="flex-row justify-center gap-2">
          <LinkButton to="/drive">
            <Trans message="Go to Drive" />
          </LinkButton>
          {retryButton}
        </Empty.Content>
      </Empty.Root>
    );
  }

  return (
    <Empty.Root>
      <Empty.Header>
        <Empty.Media>
          <CircleAlertIcon />
        </Empty.Media>
        <Empty.Title>
          {offline ? (
            <Trans message="You are offline" />
          ) : (
            <Trans message="This page did not finish loading" />
          )}
        </Empty.Title>
        <Empty.Description>
          {offline ? (
            <Trans message="Reconnect to the internet, then tap Retry. Uploads and this page will pick up from where they stopped." />
          ) : (
            <Trans message="If the connection dropped or Cloud Drive just updated, tap Retry." />
          )}
          {detail ? (
            <span className="mt-2 block font-mono text-xs text-muted-foreground">
              {detail}
            </span>
          ) : null}
        </Empty.Description>
      </Empty.Header>
      <Empty.Content>{retryButton}</Empty.Content>
    </Empty.Root>
  );
}
